// ServerRestoreDialog — restore a STORED server-side backup (Backup/Restore tab)
// into a target connection/database the user picked. Unlike RestoreDialog (which
// uploads a file and runs a two-step prepare/measure), the dump already lives on
// the server, so this is a single confirm → POST /server-backups/{id}/restore →
// ProgressModal. Easy vs advanced mirrors RestoreDialog: easy hides the options
// and forces the restore-timestamp marker on.
//
// Props:
//   backup         server_backups record (source) — { id, connectionName, database, createdAt, sizeBytes, note, gzip }
//   connectionId   target connection id (from the picker)
//   database       target database (from the picker)
//   connectionName target connection name (display)
//   createDb       bool — picker chose "new database"
//   easy           bool
//   onClose()      dismiss
//   onDone()       called after the restore job is launched (host refetches)
import { createSignal, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { restoreServerBackup } from '../lib/serverbackups.js';
import { RESTORE_STAMP_SQL } from '../lib/restore.js';
import { formatBytes } from '../lib/format.js';
import { toast } from '../lib/toast.js';

const fmtTime = (unixSec) => {
  if (!unixSec) return '';
  try { return new Date(unixSec * 1000).toLocaleString(); } catch { return String(unixSec); }
};

export default function ServerRestoreDialog(props) {
  const easy = !!props.easy;
  const [retarget, setRetarget] = createSignal(false);
  const [autoTune, setAutoTune] = createSignal(true); // default on; backend ignores if no SUPER
  const [stamp, setStamp]       = createSignal(true); // record the restore in ninocat_restore
  const [strict, setStrict]     = createSignal(false); // verify after restore (manifest-backed only)
  const [starting, setStarting] = createSignal(false);
  const canStrict = () => !!props.backup?.hasCorrectness;

  const targetLabel = () =>
    `${props.connectionName ? props.connectionName + ' · ' : ''}${props.database}${props.createDb ? ' (new)' : ''}`;

  const start = async () => {
    setStarting(true);
    try {
      await restoreServerBackup(props.backup.id, {
        connectionId: props.connectionId,
        database: props.database,
        createDb: !!props.createDb,
        retarget: easy ? false : retarget(),
        autoTune: easy ? true : autoTune(),
        strict: !easy && canStrict() && strict(),
        postSql: stamp() ? RESTORE_STAMP_SQL : '',
      });
      // Fire-and-close — the restore runs on the server and shows up in the
      // tab's "Active operations" table (including any pause-on-error prompt).
      props.onDone?.();
      props.onClose?.();
    } catch (e) {
      toast.error('Restore failed to start: ' + (e?.message || e));
    } finally {
      setStarting(false);
    }
  };

  return (
      <Modal open={true} title={`Restore backup — ${props.backup?.database || ''}`} onClose={props.onClose}>
        <div class="restore-dialog">
          <Show when={!easy}>
            <div class="restore-warning">
              ⚠ This runs the stored dump (including <span class="mono">DROP TABLE</span> /
              <span class="mono"> CREATE</span>) against the target database. Existing objects
              with the same names will be replaced. This cannot be undone.
            </div>
          </Show>

          {/* Source + target summary */}
          <div class="pk-confirm-diff">
            <div class="pk-confirm-diff-row">
              <span class="pk-confirm-col">Backup</span>
              <span class="mono">{props.backup?.filename}</span>
            </div>
            <Show when={props.backup?.note}>
              <div class="pk-confirm-diff-row">
                <span class="pk-confirm-col">Note</span>
                <span class="mono">{props.backup?.note}</span>
              </div>
            </Show>
            <div class="pk-confirm-diff-row">
              <span class="pk-confirm-col">Taken</span>
              <span class="mono">{fmtTime(props.backup?.createdAt)} · {formatBytes(props.backup?.sizeBytes || 0)}</span>
            </div>
            <div class="pk-confirm-diff-row">
              <span class="pk-confirm-col">From</span>
              <span class="mono">{props.backup?.connectionName ? props.backup.connectionName + ' · ' : ''}{props.backup?.database}</span>
            </div>
            <div class="pk-confirm-diff-row">
              <span class="pk-confirm-col">Into</span>
              <span class="mono">{targetLabel()}</span>
            </div>
          </div>

          {/* Advanced options */}
          <Show when={!easy}>
            <label class="restore-field restore-opt" style="margin-top:8px;">
              <input type="checkbox" checked={retarget()} disabled={starting()}
                     onChange={(e) => setRetarget(e.currentTarget.checked)} />
              <span>
                <span class="restore-field-label" style="display:inline;">Retarget object names to this database</span>
                <span class="muted" style="display:block;font-size:11px;">
                  Strips a source-database prefix from CREATE/DROP trigger · procedure · function · view · event names.
                </span>
              </span>
            </label>
            <label class="restore-field restore-opt">
              <input type="checkbox" checked={autoTune()} disabled={starting()}
                     onChange={(e) => setAutoTune(e.currentTarget.checked)} />
              <span>
                <span class="restore-field-label" style="display:inline;">Auto-tune <span class="mono">max_allowed_packet</span></span>
                <span class="muted" style="display:block;font-size:11px;">
                  Bumps the server global to 64 MiB during the restore (reverted after) if the account has SUPER. Affects other connections while it runs.
                </span>
              </span>
            </label>
            <label class="restore-field restore-opt">
              <input type="checkbox" checked={stamp()} disabled={starting()}
                     onChange={(e) => setStamp(e.currentTarget.checked)} />
              <span>
                <span class="restore-field-label" style="display:inline;">Append restore timestamp to database</span>
                <span class="muted" style="display:block;font-size:11px;">
                  Records this restore in a <span class="mono">ninocat_restore</span> row (NOW()), on success only.
                </span>
              </span>
            </label>
            <label class="restore-field restore-opt">
              <input type="checkbox" checked={strict() && canStrict()} disabled={starting() || !canStrict()}
                     onChange={(e) => setStrict(e.currentTarget.checked)} />
              <span>
                <span class="restore-field-label" style="display:inline;">Strict restore (verify after)</span>
                <span class="muted" style="display:block;font-size:11px;">
                  {canStrict()
                    ? 'After the restore, compares structure + per-table row counts to the backup’s manifest and logs any mismatch.'
                    : 'Unavailable — this backup has no correctness manifest (take a strict backup to enable).'}
                </span>
              </span>
            </label>
          </Show>

          <div class="restore-actions">
            <button class="btn btn-ghost" onClick={props.onClose} disabled={starting()}>Cancel</button>
            <button class="btn btn-danger" onClick={start} disabled={starting()}>
              {starting() ? '⏳ Starting…' : '⬆ Restore'}
            </button>
          </div>
        </div>
      </Modal>
  );
}
